import { useRef, useState } from 'react'
import { Bot, Send, ShoppingBag, TrendingUp, User, Wrench, Zap } from 'lucide-react'
import type { AgentChatResponse } from '@/types/domain'
import { useAgentChat, useAnalyticsAgentChat, useSupportAgentChat } from '../../src/hooks/data/useAgents'

interface Msg {
  role: 'user' | 'agent'
  text: string
  meta?: string
}

type AgentKey = 'shopping' | 'analytics' | 'support'

const AGENTS: Record<AgentKey, { label: string; icon: typeof Bot; placeholder: string; suggestions: string[] }> = {
  shopping: {
    label: 'Shopping',
    icon: ShoppingBag,
    placeholder: 'Ask about products, e.g. "search for jar products"…',
    suggestions: ['Search for jar products', 'What spices do you have?', 'Recommend something like Cow Ghee'],
  },
  analytics: {
    label: 'Analytics',
    icon: TrendingUp,
    placeholder: 'Ask about sales, funnel, or retention…',
    suggestions: ['Give me our sales summary for the last 30 days', 'What does our order funnel look like?', 'Show 6-month retention cohorts'],
  },
  support: {
    label: 'Support',
    icon: Wrench,
    placeholder: 'Ask about an order or store policy…',
    suggestions: ['What is our return policy?', 'Look up an order by id'],
  },
}

/** Real tool-calling agents (see hooks/data/useAgents.ts's doc comment) — replaces the
 * previous version of this component, which talked to a fictional `/agent` proxy that never
 * had a real backend behind it. */
export default function AiChat() {
  const [agent, setAgent] = useState<AgentKey>('shopping')
  const [input, setInput] = useState('')
  const [sessions, setSessions] = useState<Record<AgentKey, { msgs: Msg[]; sessionId?: string }>>({
    shopping: { msgs: [] },
    analytics: { msgs: [] },
    support: { msgs: [] },
  })
  const shoppingChat = useAgentChat()
  const analyticsChat = useAnalyticsAgentChat()
  const supportChat = useSupportAgentChat()
  const mutations = { shopping: shoppingChat, analytics: analyticsChat, support: supportChat }
  const pending = mutations[agent].isPending
  const endRef = useRef<HTMLDivElement>(null)

  const send = async (text: string) => {
    if (!text.trim() || pending) return
    const current = sessions[agent]
    setSessions((s) => ({ ...s, [agent]: { ...current, msgs: [...current.msgs, { role: 'user', text }] } }))
    setInput('')
    try {
      const reply: AgentChatResponse = await mutations[agent].mutateAsync({ message: text, sessionId: current.sessionId })
      setSessions((s) => ({
        ...s,
        [agent]: {
          sessionId: reply.session_id,
          msgs: [
            ...s[agent].msgs,
            {
              role: 'agent',
              text: reply.answer || '(no answer)',
              meta: reply.tools_used.length ? `tools: ${reply.tools_used.map((t) => t.tool).join(', ')}` : undefined,
            },
          ],
        },
      }))
    } catch (e: any) {
      setSessions((s) => ({
        ...s,
        [agent]: { ...s[agent], msgs: [...s[agent].msgs, { role: 'agent', text: `Error: ${e?.message ?? 'request failed'}` }] },
      }))
    }
    requestAnimationFrame(() => endRef.current?.scrollIntoView({ behavior: 'smooth' }))
  }

  const { msgs } = sessions[agent]

  return (
    <div className="flex flex-col h-[32rem] bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
      <div className="flex items-center justify-between p-3 border-b border-gray-100 dark:border-gray-700">
        <div className="flex items-center gap-2">
          <Bot className="w-5 h-5 text-blue-600" />
          <h2 className="font-semibold text-gray-800 dark:text-white">AI Agents</h2>
        </div>
        <div className="flex rounded-lg border border-gray-200 dark:border-gray-700 text-xs overflow-hidden">
          {(Object.keys(AGENTS) as AgentKey[]).map((key) => {
            const Icon = AGENTS[key].icon
            return (
              <button
                key={key}
                onClick={() => setAgent(key)}
                className={`flex items-center gap-1 px-3 py-1 ${agent === key ? 'bg-blue-600 text-white' : 'text-gray-600 dark:text-gray-300'}`}
              >
                <Icon className="w-3.5 h-3.5" /> {AGENTS[key].label}
              </button>
            )
          })}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-3 space-y-3">
        {msgs.length === 0 && (
          <p className="text-sm text-gray-400">
            {agent === 'shopping'
              ? 'Real tool-calling shopping assistant — searches the live catalog.'
              : agent === 'analytics'
                ? 'Real tool-calling analytics assistant — reads live sales/funnel/retention data.'
                : 'Real tool-calling support assistant — looks up real orders and store policy.'}
          </p>
        )}
        {msgs.map((m, i) => (
          <div key={i} className={`flex gap-2 ${m.role === 'user' ? 'justify-end' : ''}`}>
            {m.role === 'agent' && <Bot className="w-6 h-6 shrink-0 text-blue-500" />}
            <div
              className={`max-w-[80%] rounded-lg px-3 py-2 text-sm whitespace-pre-wrap ${
                m.role === 'user'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-100'
              }`}
            >
              {m.text}
              {m.meta && <div className="mt-1 text-[11px] opacity-60">{m.meta}</div>}
            </div>
            {m.role === 'user' && <User className="w-6 h-6 shrink-0 text-blue-300" />}
          </div>
        ))}
        {pending && <div className="text-sm text-gray-400 flex items-center gap-2"><Zap className="w-4 h-4 animate-pulse" /> thinking…</div>}
        <div ref={endRef} />
      </div>

      <div className="px-3 pb-2 flex flex-wrap gap-2">
        {AGENTS[agent].suggestions.map((s) => (
          <button key={s} onClick={() => send(s)} className="text-xs px-2 py-1 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200">
            {s}
          </button>
        ))}
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault()
          send(input)
        }}
        className="p-3 border-t border-gray-100 dark:border-gray-700 flex gap-2"
      >
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={AGENTS[agent].placeholder}
          className="flex-1 rounded-lg border border-gray-200 dark:border-gray-700 bg-transparent px-3 py-2 text-sm text-gray-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <button type="submit" disabled={pending} className="rounded-lg bg-blue-600 text-white px-3 disabled:opacity-50">
          <Send className="w-4 h-4" />
        </button>
      </form>
    </div>
  )
}
